import React, { useEffect, useState } from 'react'
import { AppState, DevSettings, DimensionValue, Platform } from 'react-native'
import {
  ApplicationProvider as KittenProvider,
  IconRegistry,
  ModalService,
} from '@ui-kitten/components'
import { EvaIconsPack } from '@ui-kitten/eva-icons'
import * as eva from '@eva-design/eva'
import { ActionSheetProvider } from '@expo/react-native-action-sheet'
import FlagProvider from '@unleash/proxy-client-react'
import { GestureHandlerRootView } from 'react-native-gesture-handler'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/es/integration/react'
import { SafeAreaProvider } from 'react-native-safe-area-context'
import FastImage from 'react-native-fast-image'
import { HotUpdater } from '@hot-updater/react-native'
import { setupURLPolyfill } from 'react-native-url-polyfill'
import { AutocompleteDropdownContextProvider } from 'react-native-autocomplete-dropdown'
import { StyleService } from '@src/style/service'
import { AppCheck, Bugsnag, Device, Unleash, initializeAnalyticsClient } from '@config'
import { useTheme } from '@config/theme'
import { GoogleAuth } from '@services'
import createDvaStore from '@config/state/dva'
import { Navigation } from '@navigation'
import { MaterialIconsPack, MaterialCommunityIconsPack } from '@src/config/icons'
import { createAppStateNavigator } from '@navigation/containers/AppStateNavigator'
import { BugsnagRootErrorFallback } from '@components/BugsnagRootErrorFallback'
import { AppCheckRecoveryDialog } from '@components/AppCheckRecoveryDialog'
import { NetworkProvider } from './config/state/networkProvider'
import { initializeStorage, Storage } from './utils'
import { AccessibilityProvider } from './config/accessibility'

setupURLPolyfill() // used by stream chat

if (Platform.OS === 'android') {
  // Otherwise the top insets on Android don't get taken into account when creating popovers
  // and causing elements to be placed ~20 units higher than they should be
  // this should only be enabled for Android otherwise it will have undesired effects
  ModalService.setShouldUseTopInsets = true
}

if (!Device.web && __DEV__) {
  DevSettings.addMenuItem('Clear Storage', () => {
    Storage.clear()
  })
}

AppState.addEventListener('memoryWarning', () => {
  FastImage.clearMemoryCache()
})

const App = () => {
  const { theme } = useTheme()

  return (
    <GestureHandlerRootView style={styles.container}>
      <FlagProvider unleashClient={Unleash.client} startClient={false}>
        <IconRegistry icons={[EvaIconsPack, MaterialIconsPack, MaterialCommunityIconsPack]} />
        <KittenProvider {...eva} theme={theme}>
          <ActionSheetProvider>
            <AccessibilityProvider>
              <AutocompleteDropdownContextProvider>
                <SafeAreaProvider>
                  <Navigation />
                  <AppCheckRecoveryDialog />
                </SafeAreaProvider>
              </AutocompleteDropdownContextProvider>
            </AccessibilityProvider>
          </ActionSheetProvider>
        </KittenProvider>
      </FlagProvider>
    </GestureHandlerRootView>
  )
}

const styles = StyleService.create({
  container: {
    height: Device.web ? ('100vh' as DimensionValue) : '100%',
  },
})

const AppWithStateNavigator = createAppStateNavigator(App)

const DvaApp = () => {
  const [store, setStore] = useState<ReturnType<typeof createDvaStore> | null>(null)

  useEffect(() => {
    initializeStorage()
      .then(async () => {
        if (!Device.web) {
          initializeAnalyticsClient()
          // never rejects (fails open internally); awaiting means the first
          // GraphQL requests already carry an App Check token
          await AppCheck.initialize()
        }
        setStore(createDvaStore())
      })
      .catch((error) => {
        Bugsnag.notify(error)
      })
  }, [])

  if (!store) {
    return null
  }

  return (
    <Provider store={store}>
      <NetworkProvider>
        <PersistGate persistor={store.persistor}>
          <AppWithStateNavigator />
        </PersistGate>
      </NetworkProvider>
    </Provider>
  )
}

Bugsnag.configure()
GoogleAuth.configure()

const RootErrorBoundary = Bugsnag.createErrorBoundary()

const RootApp = () => {
  if (!RootErrorBoundary) {
    return <DvaApp />
  }

  return (
    <RootErrorBoundary FallbackComponent={BugsnagRootErrorFallback}>
      <DvaApp />
    </RootErrorBoundary>
  )
}

export default HotUpdater.wrap({
  baseURL: 'https://hot-updater.nutrisense.workers.dev/api/check-update',
  updateStrategy: 'appVersion',
})(RootApp)
